import React from 'react';
import { motion } from 'framer-motion';
import { TrophyIcon, StarIcon, CheckCircleIcon } from 'lucide-react';
import { useProgress } from '../hooks/useProgress';
interface PointsCardProps {
  compact?: boolean;
}
export function PointsCard({
  compact = false
}: PointsCardProps) {
  const {
    calculatePoints,
    getOverallProgress
  } = useProgress();
  const pointsBreakdown = calculatePoints();
  const {
    completedLessons
  } = getOverallProgress();
  if (compact) {
    // Compact version for smaller spaces
    return <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-xl p-4 shadow-lg border-2 border-amber-300 relative overflow-hidden" data-id="element-2096">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full blur-2xl" data-id="element-2097"></div>
        <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full blur-xl" data-id="element-2098"></div>

        <div className="relative z-10 flex items-center justify-between" data-id="element-2099">
          <div className="flex items-center space-x-3" data-id="element-2100">
            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-2101">
              <TrophyIcon className="w-6 h-6 text-white" data-id="element-2102" />
            </div>
            <div data-id="element-2103">
              <p className="text-xs text-white/80 mb-0.5" data-id="element-2104">Punctaj Total</p>
              <p className="text-2xl font-black text-white" data-id="element-2105">
                {pointsBreakdown.totalPoints}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2" data-id="element-2106">
            <div className="text-center bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/30" data-id="element-2107">
              <p className="text-xs text-white/80" data-id="element-2108">Lecții</p>
              <p className="text-sm font-bold text-white" data-id="element-2109">
                {pointsBreakdown.lessonPoints}
              </p>
            </div>
            <div className="text-center bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/30" data-id="element-2110">
              <p className="text-xs text-white/80" data-id="element-2111">Module</p>
              <p className="text-sm font-bold text-white" data-id="element-2112">
                {pointsBreakdown.moduleBonus}
              </p>
            </div>
            <div className="text-center bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/30" data-id="element-2113">
              <p className="text-xs text-white/80" data-id="element-2114">Training</p>
              <p className="text-sm font-bold text-white" data-id="element-2115">
                {pointsBreakdown.courseBonus}
              </p>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Full version for main pages
  return <motion.div initial={{
    opacity: 0,
    y: -20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5
  }} className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-2xl border-2 border-amber-300 relative overflow-hidden" data-id="element-2116">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl" data-id="element-2117"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-2xl" data-id="element-2118"></div>

      <div className="relative z-10" data-id="element-2119">
        <div className="flex items-center justify-between mb-4" data-id="element-2120">
          <div className="flex items-center space-x-3" data-id="element-2121">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-2122">
              <TrophyIcon className="w-7 h-7 text-white" data-id="element-2123" />
            </div>
            <div data-id="element-2124">
              <h2 className="text-xl sm:text-2xl font-bold text-white" data-id="element-2125">
                Punctaj Total
              </h2>
              <p className="text-white/80 text-sm" data-id="element-2126">Progres gamificat</p>
            </div>
          </div>
          <div className="text-right" data-id="element-2127">
            <p className="text-4xl sm:text-5xl font-black text-white drop-shadow-lg" data-id="element-2128">
              {pointsBreakdown.totalPoints}
            </p>
            <p className="text-white/80 text-sm font-medium" data-id="element-2129">puncte</p>
          </div>
        </div>

        {/* Points Breakdown */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-2130">
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30" data-id="element-2131">
            <div className="flex items-center space-x-2 mb-2" data-id="element-2132">
              <StarIcon className="w-5 h-5 text-yellow-300" data-id="element-2133" />
              <p className="text-white/90 text-sm font-medium" data-id="element-2134">Lecții</p>
            </div>
            <p className="text-2xl font-bold text-white" data-id="element-2135">
              {pointsBreakdown.lessonPoints}
            </p>
            <p className="text-white/70 text-xs mt-1" data-id="element-2136">
              {completedLessons} × 10 puncte
            </p>
          </div>

          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30" data-id="element-2137">
            <div className="flex items-center space-x-2 mb-2" data-id="element-2138">
              <CheckCircleIcon className="w-5 h-5 text-green-300" data-id="element-2139" />
              <p className="text-white/90 text-sm font-medium" data-id="element-2140">Module</p>
            </div>
            <p className="text-2xl font-bold text-white" data-id="element-2141">
              {pointsBreakdown.moduleBonus}
            </p>
            <p className="text-white/70 text-xs mt-1" data-id="element-2142">
              {pointsBreakdown.moduleBonus / 25} × 25 puncte bonus
            </p>
          </div>

          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30" data-id="element-2143">
            <div className="flex items-center space-x-2 mb-2" data-id="element-2144">
              <TrophyIcon className="w-5 h-5 text-amber-300" data-id="element-2145" />
              <p className="text-white/90 text-sm font-medium" data-id="element-2146">Training-uri</p>
            </div>
            <p className="text-2xl font-bold text-white" data-id="element-2147">
              {pointsBreakdown.courseBonus}
            </p>
            <p className="text-white/70 text-xs mt-1" data-id="element-2148">
              {pointsBreakdown.courseBonus / 100} × 100 puncte bonus
            </p>
          </div>
        </div>
      </div>
    </motion.div>;
}